/**
 * Split-dropdown Run button — replaces the old RunModeToggle pair on
 * SearchPage's template flow.
 *
 * Primary action (the main button face) is "Run now". The chevron opens a
 * small menu with "Queue for later" as the secondary action. Each carries
 * a tooltip spelling out the contract so the user can predict behavior
 * without reading docs:
 *   - Run now: fires immediately if Keepa has budget. Errors with retry
 *     hint if not.
 *   - Queue for later: defers the run; a background sweep picks it up
 *     next time the bucket has budget.
 *
 * Used by SearchPage's template flow (template-launched runs skip the
 * NL-translate preview because the template's filters are already frozen).
 * The free-form NL flow still routes Run/Queue choice through the preview
 * card — see Task 9 / docs/plans/2026-05-23-searchpage-save-schedule-ux-design.md §3.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Clock, Play } from 'lucide-react';
import { Button } from '../design';

interface Props {
  onRunNow: () => void;
  onQueue: () => void;
  /** Disable both actions (e.g., missing credentials). */
  disabled?: boolean;
  /** Show the spinner on the primary face. */
  loading?: boolean;
  /** Override the primary label. Defaults to "Run now". */
  runLabel?: string;
  /** Override the dropdown label. Defaults to "Queue for later". */
  queueLabel?: string;
}

export function RunActionButton({
  onRunNow,
  onQueue,
  disabled,
  loading,
  runLabel = 'Run now',
  queueLabel = 'Queue for later',
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close the menu on outside click. We don't trap focus or stack
  // dropdowns here — single menu, single button, anywhere off it closes.
  useEffect(() => {
    if (!menuOpen) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  // Escape closes the menu — keyboard-only users can dismiss without
  // clicking elsewhere first.
  useEffect(() => {
    if (!menuOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <div ref={wrapperRef} className="relative inline-flex items-stretch">
      <Button
        type="button"
        onClick={onRunNow}
        disabled={disabled}
        loading={loading}
        leadingIcon={<Play className="size-4" />}
        title="Fires immediately if Keepa has budget. Errors with retry hint if not."
        className="rounded-r-none border-r border-r-fg-on-accent/20"
      >
        {runLabel}
      </Button>
      <Button
        type="button"
        aria-label="More run options"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((v) => !v)}
        disabled={disabled || loading}
        className="rounded-l-none px-2"
      >
        <ChevronDown className="size-3.5" />
      </Button>
      {menuOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 min-w-[14rem] bg-surface-elev border border-border-default rounded-sm shadow-lg z-10 text-sm overflow-hidden"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setMenuOpen(false);
              onQueue();
            }}
            className="flex items-center gap-2 w-full text-left px-3 py-2 hover:bg-surface text-fg-primary transition-colors duration-fast"
            title="Defers this run. A background sweep picks it up next time the bucket has budget."
          >
            <Clock className="size-3.5 text-fg-tertiary" />
            <span>{queueLabel}</span>
          </button>
        </div>
      )}
    </div>
  );
}
